import {
	capitalize,
	flatMap,
	isEqual,
	isObject,
	startCase,
	toLower,
} from "lodash-es"
import { KG_TO_LBS } from "./constants.ts"

type KgToLbsOptions = {
	suffix?: string
	onlyValue?: boolean
	decimalPlaces?: number
}

export const kgToLbs = (kg?: number, options?: KgToLbsOptions) => {
	if (kg === undefined || kg === null) {
		return 0
	}

	const { suffix = "lbs", onlyValue = false, decimalPlaces = 1 } = options || {}
	const lbsValue = kg * KG_TO_LBS
	const formattedValue = lbsValue.toFixed(decimalPlaces)

	if (onlyValue) {
		return formattedValue
	}

	return `${formattedValue} ${suffix}`
}

export const copyToClipboard = async (text: string): Promise<void> => {
	try {
		await navigator.clipboard.writeText(text)
	} catch (err) {
		console.error("Failed to copy text to clipboard", err)
	}
}

export const skipMacroTask = <T>(cb: () => T, delay = 16): Promise<T> => {
	return new Promise<T>((resolve) => {
		setTimeout(() => {
			const result = cb()
			resolve(result)
		}, delay)
	})
}

export const hasSelected = (): boolean => {
	const selection = window.getSelection()
	if (!selection) return false
	return selection.toString().length > 0
}

export const compareValues = (a: string, b: string | null) => {
	const k = a.trim()
	if (k === b) {
		return true
	}
	if (!k.length && k !== b) {
		return false
	}
	if (!k.length) return true
	return false
}

export const findFieldPathByValue = <T>(
	obj: T,
	value: any,
	path: Array<string | number | null> = [],
): null | string | Array<string | number | null> => {
	if (!isObject(obj)) return null

	const paths = flatMap(Object.entries(obj), ([k, v]) => {
		const currentPath = [...path, k]

		// Top-level match -> string
		if (isEqual(v, value)) return path.length === 0 ? [k] : currentPath

		if (Array.isArray(v)) {
			return flatMap(v, (item, i) => {
				const arrPath = [...currentPath, i]
				if (isEqual(item, value)) return path.length === 0 ? [k] : arrPath
				if (isObject(item)) return findFieldPathByValue(item, value, arrPath)
				return []
			})
		}

		if (isObject(v)) return findFieldPathByValue(v, value, currentPath)

		return []
	}).filter((k) => k !== null)

	if (paths.length === 0) {
		return null
	}

	if (paths.length === 1) {
		return paths[0] as string
	}

	return paths
}

export function sortAlpha(valA: string, valB: string) {
	return valA.localeCompare(valB, undefined, {
		numeric: true,
		sensitivity: "base",
	})
}

export function sortBy<K extends string>(field: K) {
	return <T extends Record<K, string>>(a: T, b: T) => {
		return sortAlpha(a[field], b[field])
	}
}

export const humanReadable = (str: string) =>
	capitalize(startCase(toLower(str)))
